import dayjs from 'dayjs';
import random from 'lodash/random';
import isEmpty from 'lodash/isEmpty';
import axios from 'axios';

import { v4 as uuidv4 } from 'uuid';

import wsMessageTypes from 'constants/wsMessageTypes';
import {
  getIsDataPersistent,
  getToken,
  getGoogleAnalyticsEnabled,
  getFeedback,
  getApiUrlDomain,
  getIsPreviewMode,
} from 'store/services/session/selectors';
import { fakeFileUpload } from 'store/preview/mockUploader';
import * as types from './actionTypes';
import { getChatData, getIsDialogEnded, getThread } from './selectors';
import { setPersistentChatData } from '../session/actions';
import { hasError, resetError } from '../errors';
import {
  AttachmentsConfig,
  FeedbackType,
  MessageOwner,
  MessageStatus,
  UrlSelectMethod,
  MetaPayload,
  TextMessage,
} from 'types';
import { googleAnalytics } from 'services/googleAnalytics';
import {
  getStorageItem,
  removeStorageItem,
  setStorageItem,
} from 'services/storage';
import { STORAGE_KEY } from 'constants/browserStorageKeys';
import { fileErrorEvents, fileErrors } from 'constants/fileErrors';
import initialState from './initialState';
import { i18n } from 'utils/i18n';
import { THREAD_ITEM_TYPE } from 'constants/chat';

export const setIsHistory = (isHistory: boolean) => ({
  type: types.SET_IS_HISTORY,
  payload: { isHistory },
});

export const setDebugInfo = data => ({
  type: types.SET_DEBUG_INFO,
  payload: data,
});

export const updateThreadMessage = message => ({
  type: types.UPDATE_THREAD_MESSAGE,
  payload: message,
});

export const deleteThreadMessage = id => ({
  type: types.DELETE_THREAD_MESSAGE,
  payload: id,
});

export const saveMetaMessage = (meta: MetaPayload) => dispatch => {
  dispatch({
    type: types.SAVE_META_MESSAGE,
    payload: {
      meta,
    },
  });
};

export const saveBotPromptsResponse = ({
  message,
  sequence_id,
  client_message_id = 0,
  status,
  source,
  type,
  isSensitive = false,
}) => (dispatch, getState) => {
  const { withAgent, isMinimized, isOpen } = getState().chat;

  dispatch({
    type: types.SAVE_BOT_RESPONSE,
    payload: {
      message: {
        status,
        message,
        withAgent,
        sequence_id,
        client_message_id,
        type,
        owner: source,
        timestamp: dayjs().format(),
        ...(isSensitive && { isSensitive: true }),
      },
    },
  });

  if (!document.hidden && !isMinimized && isOpen) {
    setStorageItem(STORAGE_KEY.LAST_SEEN_MESSAGE_ID, sequence_id);
  }
};

export const saveBotTypingOn = () => ({
  type: types.SAVE_BOT_TYPING_ON,
});

export const saveBotTypingOff = () => ({
  type: types.SAVE_BOT_TYPING_OFF,
});

// Set chat data temporarily - will be erased after closing currently opened chat (session)
export const setSessionChatData = data => ({
  type: types.SET_SESSION_CHAT_DATA,
  payload: data,
});

export const setChatData = data => (dispatch, getState) => {
  if (getIsDataPersistent(getState())) {
    dispatch(setPersistentChatData(data));
  } else {
    dispatch(setSessionChatData(data));
  }
};

export const startChat = (
  utterance = null,
  semantics = null,
  input_fields = null,
) => async (dispatch, getState, ws) => {
  try {
    dispatch({
      type: types.START_CHAT_REQUEST,
    });
    dispatch(setAttachmentsConfig(initialState.attachmentsConfig));

    const state = getState();
    const data = getChatData(state);
    const client_message_id = uuidv4();
    const session_id = getStorageItem(STORAGE_KEY.SESSION_ID);

    if (session_id) {
      ws.send(
        JSON.stringify({
          type: wsMessageTypes.SESSION_RESUME_REQUEST,
          api_key: getToken(getState()),
          session_id,
        }),
      );
    } else {
      ws.send(
        JSON.stringify({
          type: wsMessageTypes.START_SESSION_REQUEST,
          api_key: getToken(getState()),
          session_id,
          client_message_id,
          utterance,
          semantics,
          input_fields: {
            ...data,
            ...input_fields,
          },
        }),
      );
    }
  } catch (error) {
    dispatch({
      type: types.START_CHAT_ERROR,
      payload: {
        error: error.message,
      },
    });
  }
};

interface OpenChatParams {
  utterance?: string;
  semantics?: any;
  input_fields?: any;
  isReopen?: boolean;
  isFormOpen?: boolean;
}

export const openChat = (
  {
    utterance,
    semantics,
    input_fields,
    isReopen,
    isFormOpen,
  }: OpenChatParams = {
    utterance: '',
    semantics: null,
    input_fields: null,
    isReopen: false,
    isFormOpen: false,
  },
) => (dispatch, getState) => {
  const gaEnabled = getGoogleAnalyticsEnabled(getState());
  const { onChatOpened } = googleAnalytics(gaEnabled);
  const { isOpen } = getState().chat;

  if (!isOpen || isFormOpen) {
    dispatch(openWS());
    dispatch(openChatBox(isReopen));
    dispatch(startChat(utterance, semantics, input_fields));
    onChatOpened(window.location.href);
  }
};
export const startChatSuccess = (dialogId: string) => ({
  type: types.START_CHAT_SUCCESS,
  payload: { id: dialogId },
});

export const setAttachmentsConfig = (
  attachmentsConfig: Partial<AttachmentsConfig>,
) => ({
  type: types.SET_ATTACHMENTS_CONFIG,
  payload: { attachmentsConfig },
});

export const setErrorMessage = (errorMessage: string) => ({
  type: types.SET_MESSAGE_ERROR,
  payload: { errorMessage },
});

export const sendFileMessage = (files: File[], utterance: string) => async (
  dispatch,
  getState,
) => {
  try {
    const client_message_id = uuidv4();

    dispatch({
      type: types.SAVE_USER_RESPONSE,
      payload: {
        message: {
          owner: MessageOwner.USER,
          type: 'file',
          message: {
            utterance,
            blobFiles: files,
            files: files.map(file => ({
              file_name: file.name,
              file_type: file.type,
              file_size: file.size,
            })),
          },
          client_message_id,
          timestamp: dayjs().format(),
          status: MessageStatus.PENDING,
        },
      },
    });

    const state = getState();

    if (getIsPreviewMode(state)) {
      await fakeFileUpload(files, utterance, client_message_id, dispatch);
      return;
    }

    const session_id = getStorageItem(STORAGE_KEY.SESSION_ID);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    formData.append('session_id', session_id);
    formData.append('utterance', utterance);
    formData.append('client_message_id', client_message_id);

    await axios({
      method: 'post',
      url: `https://${getApiUrlDomain(state)}/chat/attachments`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${getToken(state)}`,
      },
    });
  } catch (error) {
    const errorCode =
      error.code === 'ERR_NETWORK'
        ? fileErrorEvents.ERROR_NETWORK
        : error?.response?.data?.error_code;
    dispatch(setErrorMessage(i18n.t(fileErrors[errorCode])));
  }
};

export const sendTextMessage = ({
  utterance = '',
  semantics = null,
  input_fields = null,
  rich_content,
  isSensitive = false,
}: TextMessage) => async (dispatch, getState, ws) => {
  try {
    const state = getState();
    const data = getChatData(state);
    const isDialogEnded = getIsDialogEnded(state);
    const client_message_id = uuidv4();
    const session_id = getStorageItem(STORAGE_KEY.SESSION_ID);

    dispatch({
      type: types.SAVE_USER_RESPONSE,
      payload: {
        message: {
          owner: MessageOwner.USER,
          type: 'message',
          message: utterance,
          client_message_id,
          timestamp: dayjs().format(),
          status: MessageStatus.PENDING,
          ...(isSensitive && { isSensitive: true }),
        },
      },
    });

    if (isSensitive) {
      dispatch(setSensitiveInput(false));
    }

    if (isDialogEnded) {
      await ws.send(
        JSON.stringify({
          type: wsMessageTypes.START_DIALOG_REQUEST,
          api_key: getToken(state),
          session_id,
          client_message_id,
          utterance,
          input_fields: {
            ...data,
            ...input_fields,
          },
          rich_content,
          semantics,
        }),
      );

      dispatch(setDialogEnded(false));
    } else {
      setStorageItem(STORAGE_KEY.VIEW_FEEDBACK, false);
      dispatch({
        type: types.SEND_TEXT_MESSAGE_REQUEST,
      });

      await ws.send(
        JSON.stringify({
          type: wsMessageTypes.DIALOG_REQUEST,
          api_key: getToken(state),
          session_id,
          client_message_id,
          utterance,
          input_fields,
          rich_content,
          semantics,
        }),
      );

      dispatch({
        type: types.SEND_TEXT_MESSAGE_SUCCESS,
      });
    }
  } catch (error) {
    dispatch(saveBotTypingOff());
    dispatch({
      type: types.SEND_TEXT_MESSAGE_ERROR,
      payload: {
        error: error.message,
      },
    });
  }
};

export const resendMessage = (messageId: string, files?: File[]) => (
  dispatch,
  getState,
) => {
  const thread = getThread(getState());

  thread.forEach(threadMessage => {
    if (threadMessage.client_message_id !== messageId) return null;

    if (files?.length) {
      dispatch(sendFileMessage(files, threadMessage.message.utterance));
    } else {
      dispatch(sendTextMessage({ utterance: threadMessage.message }));
    }
    dispatch(deleteThreadMessage(messageId));
  });
};

export const sendTypingOnMessage = () => async (dispatch, getState, ws) => {
  const session_id = getStorageItem(STORAGE_KEY.SESSION_ID);

  try {
    dispatch({
      type: types.SEND_TYPING_REQUEST,
    });

    ws.send(
      JSON.stringify({
        type: wsMessageTypes.TYPING_REQ,
        api_key: getToken(getState()),
        session_id,
        typing_on: true,
      }),
    );

    dispatch({
      type: types.SEND_TYPING_SUCCESS,
    });
  } catch (error) {
    await dispatch({
      type: types.SEND_TYPING_ERROR,
      payload: {
        error: error.message,
      },
    });
  }
};

export const sendTypingOffMessage = () => async (dispatch, getState, ws) => {
  const session_id = getStorageItem(STORAGE_KEY.SESSION_ID);

  try {
    dispatch({
      type: types.SEND_TYPING_REQUEST,
    });

    ws.send(
      JSON.stringify({
        type: wsMessageTypes.TYPING_REQ,
        api_key: getToken(getState()),
        session_id,
        typing_on: false,
      }),
    );

    dispatch({
      type: types.SEND_TYPING_SUCCESS,
    });
  } catch (error) {
    await dispatch({
      type: types.SEND_TYPING_ERROR,
      payload: {
        error: error.message,
      },
    });
  }
};

export const openWS = () => async (dispatch, getState, ws) => {
  try {
    if ([ws.CLOSED, ws.CLOSING].includes(ws.readyState)) ws.reconnect();
  } catch (error) {
    await dispatch({
      type: types.START_CHAT_ERROR,
      payload: {
        error: error.message,
      },
    });
  }
};

export const openChatBox = (isReopen = false) => dispatch => {
  !isReopen && setStorageItem(STORAGE_KEY.REOPEN_FEEDBACK, false);

  dispatch({
    type: types.OPEN_CHAT_BOX,
  });
};

export const minimizeChatBox = (isMinimized: boolean) => ({
  type: types.TOGGLE_MINIMIZE_CHAT_BOX,
  payload: { isMinimized },
});

export const startRetryConnect = () => ({
  type: types.START_RETRY_CONNECT,
});

export const stopRetryConnect = () => ({
  type: types.STOP_RETRY_CONNECT,
});

export const closeChatBox = () => (dispatch, getState, ws) => {
  const gaEnabled = getGoogleAnalyticsEnabled(getState());
  const { onChatClosed } = googleAnalytics(gaEnabled);
  const feedback = getFeedback(getState());
  const viewFeedback = getStorageItem(STORAGE_KEY.VIEW_FEEDBACK);
  const reopenFeedback = getStorageItem(STORAGE_KEY.REOPEN_FEEDBACK);
  const session_id = getStorageItem(STORAGE_KEY.SESSION_ID);

  dispatch({
    type: types.CLOSE_CHAT_BOX,
  });

  dispatch(resetError());

  if (session_id) {
    ws.send(
      JSON.stringify({
        type: wsMessageTypes.STOP_SESSION_REQ,
        api_key: getToken(getState()),
        session_id,
      }),
    );
  }

  if (!viewFeedback && !reopenFeedback && feedback?.type) {
    setTimeout(() => {
      setStorageItem(STORAGE_KEY.REOPEN_FEEDBACK, true);
      dispatch(setFeedback());
    }, 1000);
  } else {
    setStorageItem(STORAGE_KEY.REOPEN_FEEDBACK, false);
  }

  removeStorageItem(STORAGE_KEY.SESSION_ID);
  removeStorageItem(STORAGE_KEY.TRANSFER_ID);
  removeStorageItem(STORAGE_KEY.WELCOME_FORM);
  removeStorageItem(STORAGE_KEY.MINIMIZED_CHAT);
  removeStorageItem(STORAGE_KEY.LAST_SEEN_MESSAGE_ID);

  ws.close();
  onChatClosed(window.location.href);

  /**
   * * The WebChat will not be reconnecting if previous connection was never established and the ws.close(); method was fired.
   * * To fix this issue we have to manually set _connectLock = false after ws.close();
   * * See this https://github.com/pladaria/reconnecting-websocket/issues/165
   */
  ws._connectLock = false;
};

export const restartChat = () => async (dispatch, getState, ws) => {
  const session_id = getStorageItem(STORAGE_KEY.SESSION_ID);

  await dispatch({
    type: types.RESTART_CHAT_REQUEST,
  });

  if (session_id) {
    await ws.send(
      JSON.stringify({
        type: wsMessageTypes.STOP_SESSION_REQ,
        api_key: getToken(getState()),
        session_id: getStorageItem(STORAGE_KEY.SESSION_ID),
      }),
    );
    ws.close();
  }

  removeStorageItem(STORAGE_KEY.SESSION_ID);
  removeStorageItem(STORAGE_KEY.TRANSFER_ID);
  removeStorageItem(STORAGE_KEY.LAST_SEEN_MESSAGE_ID);

  ws.reconnect();
  await startChat()(dispatch, getState, ws);

  await dispatch({
    type: types.RESTART_CHAT_SUCCESS,
  });
};

export function updateDialogId(id) {
  return {
    type: types.UPDATE_DIALOG_ID,
    payload: { id },
  };
}

export const setSessionExpired = () => dispatch => {
  dispatch(saveBotTypingOff());
  dispatch(setDisableChat(true));
  dispatch(setDialogEnded(true));
  dispatch(setAttachmentsConfig(initialState.attachmentsConfig));

  dispatch({
    type: types.SET_SESSION_EXPIRED,
  });
};

export const setAgentConnecting = (isAgentConnecting: boolean) => ({
  type: types.SET_AGENT_CONNECTING,
  payload: { isAgentConnecting },
});

export const setAgentTimeout = (isAgentTimeout: boolean) => ({
  type: types.SET_AGENT_TIMEOUT,
  payload: { isAgentTimeout },
});

export const setSensitiveInput = (isSensitiveInput: boolean) => ({
  type: types.SET_SENSITIVE_INPUT,
  payload: { isSensitiveInput },
});

export const setAgentConnected = (isAgentConnected: boolean) => (
  dispatch,
  getState,
) => {
  dispatch({
    type: types.SET_AGENT_CONNECTED,
    payload: { isAgentConnected },
  });

  if (isAgentConnected) {
    const thread = getThread(getState());
    const hasAgentJoinedNotification = thread.some(
      item => item.type === THREAD_ITEM_TYPE.AGENT_JOINED,
    );

    if (!hasAgentJoinedNotification) {
      dispatch({
        type: types.SET_DIALOG_META,
        payload: {
          message: {
            type: THREAD_ITEM_TYPE.AGENT_JOINED,
            timestamp: dayjs().format(),
          },
        },
      });
    }
  }
};

export const setDialogEnded = (isDialogEnded: boolean) => ({
  type: types.SET_DIALOG_ENDED,
  payload: { isDialogEnded },
});

export const setDisableChat = (isChatDisabled: boolean) => ({
  type: types.SET_DISABLE_CHAT,
  payload: { isChatDisabled },
});

export const setActiveApiUrl = (activeApiUrl: string) => ({
  type: types.SET_ACTIVE_API_URL,
  payload: {
    activeApiUrl,
  },
});

export const clearFailedApiUrls = () => ({
  type: types.CLEAR_FAILED_API_URLS,
});

export const setFailedApiUrl = (failedUrl: string) => dispatch => {
  dispatch(setActiveApiUrl(''));

  dispatch({
    type: types.SET_FAILED_API_URL,
    payload: { failedUrl },
  });
};

export const setOrderedApiUrl = (
  apiUrls: string[],
  activeApiUrls: string[],
  failedApiUrls: string[],
  dispatch,
) => {
  if (isEmpty(failedApiUrls)) {
    dispatch(setActiveApiUrl(apiUrls[0]));
  } else if (!isEmpty(activeApiUrls)) {
    const prevFailedUrl = failedApiUrls[failedApiUrls.length - 1];
    const nextUrlIndex = apiUrls.indexOf(prevFailedUrl) + 1;
    const nextUrl = apiUrls[nextUrlIndex];

    dispatch(setActiveApiUrl(nextUrl));
  } else {
    dispatch(clearFailedApiUrls());
  }
};

export const setRoundRobinApiUrl = (
  apiUrls: string[],
  activeApiUrls: string[],
  dispatch,
) => {
  const emptyActiveApiUrls = isEmpty(activeApiUrls);

  if (emptyActiveApiUrls) {
    dispatch(clearFailedApiUrls());
  }

  const activeApiUrl = emptyActiveApiUrls
    ? apiUrls[random(apiUrls.length - 1)]
    : activeApiUrls[random(activeApiUrls.length - 1)];

  dispatch(setActiveApiUrl(activeApiUrl));
};

export const setApiUrl = (
  apiUrls: string[],
  urlSelectMethod: UrlSelectMethod,
) => (dispatch, getState) => {
  if (!apiUrls?.length) {
    return null;
  }

  const { failedApiUrls } = getState().chat;
  const activeApiUrls = apiUrls.filter(
    url => !failedApiUrls.some(failedUrl => failedUrl === url),
  );

  if (urlSelectMethod === 'ROUND_ROBIN') {
    setRoundRobinApiUrl(apiUrls, activeApiUrls, dispatch);
  } else if (urlSelectMethod === 'ORDERED') {
    setOrderedApiUrl(apiUrls, activeApiUrls, failedApiUrls, dispatch);
  }
};

export const setChatWithAgent = (withAgent: boolean) => ({
  type: types.SET_CHAT_WITH_AGENT,
  payload: { withAgent },
});

export const setFeedback = () => (dispatch, getState) => {
  const {
    session: {
      config: { feedback },
    },
    chat: { withAgent },
  } = getState();

  const matchFeedbackType =
    feedback.type === FeedbackType.ALWAYS ||
    (feedback.type === FeedbackType.AGENT && withAgent) ||
    (feedback.type === FeedbackType.BOT && !withAgent);

  if (matchFeedbackType && !feedback?.form) {
    const transfer_id = getStorageItem(STORAGE_KEY.TRANSFER_ID);

    dispatch(
      sendTextMessage({
        input_fields: {
          Intent: 'provide_feedback',
          previous_dialog_id: getStorageItem(STORAGE_KEY.PREVIOUS_DIALOG_ID),
          ...(transfer_id && { transfer_id }),
        },
      }),
    );
    setStorageItem(STORAGE_KEY.VIEW_FEEDBACK, true);
  } else if (matchFeedbackType) {
    dispatch(openChatBox(true));
  }

  dispatch({
    type: types.SET_FEEDBACK_FORM,
    payload: { matchFeedbackType },
  });
};

export const restoreConnection = () => (dispatch, getState, ws) => {
  const thread = getThread(getState());
  const connectionError = hasError(getState());

  thread.forEach(message => {
    if (
      message.owner === MessageOwner.USER &&
      message.status === MessageStatus.PENDING &&
      connectionError
    ) {
      dispatch(deleteThreadMessage(message.client_message_id));
      // Sensitive messages are never auto-resent on reconnect. The thread stores
      // the real plaintext utterance (the dot mask is applied only at render time
      // and in the PDF transcript), so we skip resend here — guarded by the
      // isSensitive flag — to avoid re-transmitting the secret.
      if (!message.isSensitive) {
        dispatch(sendTextMessage({ utterance: message.message }));
      }
      dispatch(resetError());
    }
  });
};
